import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { getCachedBillingData, getPartialBillingData } from '../data/api';
import { logError, setContext } from '../utils/sentry-logger';
import getTrialDaysRemaining from '../utils/get-trial-days-remaining';
import Product from './Plans/Product';
import CancelModal from './shared/CancelModal';
import formatNumber from '../utils/format-number';
import { getPurchasableProducts, createProductIdFromStripeId } from '../utils/billing-utils';
import CouponBanner from './Plans/CouponBanner';
import Loading from './shared/Loading';
import {
    BILLING_PERIODS,
    DEFAULT_BILLING_PERIOD,
    LAPSED_TRIAL_CHECKOUT_STATE_KEY,
    MEMBER_MIN_COUNT,
    ROUTES,
    SENDING_DOMAIN_STATES,
} from '../utils/constants';
import { applyDevPlanOverride } from '../dev-scenarios/browser-overrides';
import { findRecommendedPlan } from '../utils/plan-recommendation';
import { hasActiveSubdirectory } from '../utils/site-utils';
import getForceUpgradeStatus, { FORCE_UPGRADE_STATUS } from '../utils/get-force-upgrade-status';

/**
 * @typedef {import('../types').BillingData} BillingData
 * @typedef {object} RedirectScenario
 * @property {boolean} shouldRedirectToCheckout
 * @property {boolean} shouldRedirectToDashboard
 * @property {boolean} isDunning
 * @property {boolean} isForceUpgrade
 * @typedef {object} PlansProps
 * @property {(visible: boolean) => void} showNav
 * @property {(title: string) => void} updateTitle
 * @property {import('history').History} history
 */

function ExternalLinkIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" height="12" width="12">
            <g transform="matrix(0.5,0,0,0.5,0,0)">
                <line
                    style={{
                        fill: 'none',
                        stroke: '#000000',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                        strokeWidth: '1.5px',
                    }}
                    x1="9.25"
                    y1="14.523"
                    x2="23.25"
                    y2="0.75"
                ></line>
                <polyline
                    style={{
                        fill: 'none',
                        stroke: '#000000',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                        strokeWidth: '1.5px',
                    }}
                    points="23.25 8.621 23.25 0.75 15.25 0.75"
                ></polyline>
                <path
                    style={{
                        fill: 'none',
                        stroke: '#000000',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                        strokeWidth: '1.5px',
                    }}
                    d="M12.125,5.75H1.625a.875.875,0,0,0-.875.875v15.75a.875.875,0,0,0,.875.875h15.75a.875.875,0,0,0,.875-.875v-10.5"
                ></path>
            </g>
        </svg>
    );
}

let _isMounted = false;

/**
 * Resolves route precedence before rendering plan selection.
 * @param {BillingData|null|undefined} cachedBillingData
 * @returns {RedirectScenario}
 */
function getRedirectScenario(cachedBillingData) {
    const isCustomPlan =
        cachedBillingData?.currentPrice?.base_product?.toLowerCase().indexOf('custom') >= 0;
    const hasErrorOrNoBillingData = !cachedBillingData || cachedBillingData?.error;
    const hasNoAvailableProducts = !cachedBillingData?.availableProducts;
    const forceUpgradeStatus = getForceUpgradeStatus(cachedBillingData);
    const isForceUpgrade = forceUpgradeStatus === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER;
    const isIncompleteForceUpgrade = forceUpgradeStatus === FORCE_UPGRADE_STATUS.INCOMPLETE_OWNER;
    const hasNoForceUpgrade = forceUpgradeStatus === FORCE_UPGRADE_STATUS.NONE;
    const shouldLeavePlanSelection =
        !hasNoForceUpgrade && !isForceUpgrade && !isIncompleteForceUpgrade;
    const isDunning = cachedBillingData?.isGrace;

    const willCancelAtPeriodEnd = cachedBillingData?.willCancelAtPeriodEnd;
    const hasUnavailableBillingData = hasErrorOrNoBillingData || hasNoAvailableProducts;

    const shouldRedirectToCheckout =
        isIncompleteForceUpgrade ||
        (hasNoForceUpgrade &&
            (cachedBillingData?.isIncomplete || cachedBillingData?.isAutoBillingGrace));
    const shouldRedirectToDashboard =
        shouldLeavePlanSelection ||
        (hasNoForceUpgrade &&
            (willCancelAtPeriodEnd || isCustomPlan || isDunning || hasUnavailableBillingData));

    return {
        shouldRedirectToCheckout,
        shouldRedirectToDashboard,
        isDunning,
        isForceUpgrade,
    };
}

function logNoPurchasableProductsError(cachedBillingData) {
    setContext('plans.getPurchasableProducts', {
        siteId: cachedBillingData?.currentSite?.id,
        product_group: cachedBillingData?.user?.show_product_group,
    });
    logError(new Error('No purchasable products found for user in `/plans`'), { level: 'warning' });
}

/**
 * Renders plan selection after redirecting states that cannot change plans.
 * @param {PlansProps} props
 * @returns {React.ReactNode}
 */
export function Plans({ showNav, updateTitle, history }) {
    const [loading, setLoading] = useState(true);
    const [showAnnual, setShowAnnual] = useState(true);
    const [closeModal, setCloseModal] = useState(false);
    const [billingData, setBillingData] = useState(null);
    const [products, setProducts] = useState(null);
    const [currentMembers, setCurrentMembers] = useState(null);
    const [currentSiteLimits, setCurrentSiteLimits] = useState(null);

    useEffect(() => {
        if (loading) {
            showNav(false);
        }

        if (!_isMounted) {
            _isMounted = true;

            const fetchCachedBillingData = async () => {
                const cachedBillingData = await getCachedBillingData();

                const {
                    shouldRedirectToCheckout,
                    shouldRedirectToDashboard,
                    isDunning,
                    isForceUpgrade,
                } = getRedirectScenario(cachedBillingData);

                setContext('plans', {
                    siteId: cachedBillingData?.currentSite?.id,
                });

                setBillingData(cachedBillingData);

                if (shouldRedirectToCheckout) {
                    // Redirect back to Checkout with the current plan to finish the payment
                    return history.push(cachedBillingData?.checkoutRoute || ROUTES.DASHBOARD);
                }

                if (shouldRedirectToDashboard && isDunning) {
                    // TODO: why are we showing the nav here already?
                    showNav(true);
                }

                if (shouldRedirectToDashboard) {
                    // Redirect back to Dashboard
                    return history.push(ROUTES.DASHBOARD);
                }

                // Ensure we always load fresh limit data from Daisy in case anything has changed (e. g. theme or number of staff)
                const freshCurrentSiteLimits = await getPartialBillingData('currentSiteLimits');

                let membersLimit = MEMBER_MIN_COUNT;

                const freshMembersTotal = freshCurrentSiteLimits?.members?.total;
                const cachedMembersTotal = cachedBillingData?.currentSiteLimits?.members?.total;
                const currentMembersTotal = Number.isFinite(freshMembersTotal)
                    ? freshMembersTotal
                    : Number.isFinite(cachedMembersTotal)
                      ? cachedMembersTotal
                      : MEMBER_MIN_COUNT;
                membersLimit = currentMembersTotal === 0 ? membersLimit : currentMembersTotal;

                const effectiveCurrentSiteLimits = {
                    ...(cachedBillingData?.currentSiteLimits || {}),
                    ...(freshCurrentSiteLimits || {}),
                    members: {
                        ...(cachedBillingData?.currentSiteLimits?.members || {}),
                        ...(freshCurrentSiteLimits?.members || {}),
                        total: currentMembersTotal,
                    },
                };

                // Don't show inactive products, e.g., a legacy plan the customer is currently on.
                const sortedProducts = getPurchasableProducts(
                    cachedBillingData?.availableProducts,
                    cachedBillingData?.user?.show_product_group,
                ).filter((product) => product?.active !== 0);

                if (!sortedProducts || !sortedProducts.length) {
                    logNoPurchasableProductsError(cachedBillingData);

                    if (!isForceUpgrade && !cachedBillingData?.isTrial) {
                        return history.push(ROUTES.DASHBOARD);
                    }
                }

                setCurrentSiteLimits(effectiveCurrentSiteLimits);
                setProducts(sortedProducts);
                setCurrentMembers(membersLimit);
                setLoading(false);
                showNav(true);
            };

            fetchCachedBillingData();
        }
        return () => {
            _isMounted = false;
        };
    }, []);

    useEffect(() => {
        const updateHeaderTitle = async ({
            isTrial,
            trialEnd,
            isForceUpgrade,
            lastSubscriptionType,
        }) => {
            if (isTrial && trialEnd) {
                const trialEndInDays = getTrialDaysRemaining(trialEnd);
                let trialWording = '';

                switch (trialEndInDays) {
                    case 0:
                        trialWording = '<strong>today</strong>';
                        break;
                    case 1:
                        trialWording = `in <strong>${trialEndInDays} day</strong>`;
                        break;
                    default:
                        trialWording = `in <strong>${trialEndInDays} days</strong>`;
                        break;
                }

                updateTitle(
                    `Your trial ends ${trialWording}. You can pick a plan now to start your subscription and unlock the full features of Ghost(Pro) right away.`,
                );
            } else if (isForceUpgrade && lastSubscriptionType === 'trial') {
                updateTitle(
                    'Your free trial is over! Pick a plan now, and unlock the full features of Ghost(Pro)',
                );
            } else if (!isTrial) {
                updateTitle('Select a new plan to fit your needs.');
            } else {
                updateTitle('Pick a plan now to start a subscription and keep your work online.');
            }
        };
        if (_isMounted) {
            updateHeaderTitle({
                isTrial: billingData?.isTrial,
                trialEnd: billingData?.subscription?.trial_end,
                isForceUpgrade: billingData?.isForceUpgrade,
                lastSubscriptionType: billingData?.lastSubscriptionType,
            });
        }
        return () => {
            updateHeaderTitle('Billing and domain settings');
        };
    }, [
        billingData?.isTrial,
        updateTitle,
        billingData?.subscription?.trial_end,
        billingData?.isForceUpgrade,
        billingData?.lastSubscriptionType,
    ]);

    const handleNestedLoadingState = (value) => {
        setLoading(value);
        showNav(!value);
    };

    const handleToggleAnnual = (event) => {
        setShowAnnual(event.target.checked);
    };

    const handlePlanSelected = (stripeId, period) => {
        period = period ?? (showAnnual ? DEFAULT_BILLING_PERIOD : BILLING_PERIODS.MONTH);

        try {
            const productId = createProductIdFromStripeId(stripeId);
            const checkoutRoute = `/checkout/${productId}-${period}`;
            const isLapsedTrialCheckout =
                getForceUpgradeStatus(billingData) === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER;

            if (isLapsedTrialCheckout) {
                return history.push(checkoutRoute, {
                    [LAPSED_TRIAL_CHECKOUT_STATE_KEY]: true,
                });
            }

            return history.push(checkoutRoute);
        } catch (error) {
            logError(error, { level: 'warning' });
            return history.push('/');
        }
    };

    const handleOpenModal = (value) => {
        setCloseModal(value);
    };

    const handleCloseModal = () => {
        setCloseModal(false);
    };

    // Dev-only (NODE_ENV=development): swaps in the selected current-plan override; a no-op in prod.
    const effectiveCurrentPrice = applyDevPlanOverride(
        billingData?.currentPrice,
        products,
        currentMembers,
    );

    const selectedPeriod = showAnnual ? BILLING_PERIODS.YEAR : BILLING_PERIODS.MONTH;
    const isSubdirectory = hasActiveSubdirectory(billingData?.currentSite);
    const hasCustomSendingDomain =
        billingData?.currentSite?.sending_domain_status === SENDING_DOMAIN_STATES.VERIFIED;

    const recommendation = findRecommendedPlan({
        availableProducts: products || [],
        productCatalog: billingData?.availableProducts || products || [],
        currentMembers,
        currentSiteLimits,
        showPeriod: selectedPeriod,
        currentPrice: effectiveCurrentPrice,
        isSubdirectory,
        currentGhostVersion: billingData?.currentGhostVersion,
        hasCustomSendingDomain,
    });

    return (
        <main className="main main-wide">
            {loading ? (
                <Loading />
            ) : (
                <>
                    {billingData?.discount?.coupon && billingData?.user?.coupon && (
                        <CouponBanner
                            discount={billingData?.discount}
                            isTrial={billingData?.isTrial}
                        />
                    )}
                    <div
                        className={products?.length === 3 ? 'planselect plan-3' : 'planselect'}
                        data-testid="plans"
                    >
                        <section className="plancalc">
                            <header>
                                {currentMembers === 0 ? (
                                    <h3>
                                        Including an audience <br></br>of up to
                                    </h3>
                                ) : (
                                    <h3>
                                        Based on your total <br></br>audience size of
                                    </h3>
                                )}
                                <div className="plancalc-memberscount">
                                    <div className="plancalc-memberscount-number">
                                        {formatNumber(currentMembers)}
                                    </div>
                                    <div className="plancalc-memberscount-label">Members</div>
                                </div>

                                <a
                                    href="https://ghost.org/pricing/?ref=billing.ghost.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View detailed pricing table
                                    <span className="link-ext">
                                        <ExternalLinkIcon />
                                    </span>
                                </a>

                                <p>
                                    Members are people who have signed up to your site. They can log
                                    in, receive email newsletters, or buy paid subscriptions.
                                </p>
                            </header>
                            <footer>
                                <div>
                                    <strong>Annual discount</strong>
                                    <p className="annual-discount">2 months free!</p>
                                </div>
                                <span>
                                    <input
                                        id="interval"
                                        className="toggle-btn"
                                        type="checkbox"
                                        name="interval"
                                        defaultChecked
                                        onChange={handleToggleAnnual}
                                    />
                                    <label className="hidden" htmlFor="interval"></label>
                                </span>
                            </footer>
                        </section>

                        {products.map((product) => (
                            <Product
                                productName={product?.name}
                                stripeProductId={product?.stripeId}
                                addons={product?.addons}
                                prices={product?.prices}
                                limits={product?.limits}
                                features={product?.features}
                                currentMembers={currentMembers}
                                handlePlanSelected={handlePlanSelected}
                                key={product?.name}
                                showPeriod={selectedPeriod}
                                isSubdirectory={isSubdirectory}
                                hasCustomSendingDomain={hasCustomSendingDomain}
                                currentPrice={effectiveCurrentPrice}
                                currentGhostVersion={billingData?.currentGhostVersion}
                                currentSiteLimits={currentSiteLimits}
                                availableProducts={products}
                                currentPlanName={effectiveCurrentPrice?.base_product || null}
                                recommendedPlanName={
                                    recommendation.hasAudienceBlocker
                                        ? recommendation.product?.name || null
                                        : null
                                }
                                recommendedIsCurrentPlan={recommendation.isCurrentPlan}
                                isRecommended={
                                    recommendation.hasAudienceBlocker &&
                                    recommendation.product?.name === product?.name &&
                                    !recommendation.isCurrentPlan
                                }
                            />
                        ))}
                    </div>

                    <footer
                        className={`planselector-footer ${billingData?.isTrial ? 'two-columns' : ''}`}
                    >
                        <div className="footer-cancel">
                            {billingData?.isTrial && (
                                <CancelModal
                                    subscription={billingData?.subscription}
                                    setLoadingState={handleNestedLoadingState}
                                    onCloseModal={handleCloseModal}
                                    setModalIsOpen={handleOpenModal}
                                    modalIsOpen={closeModal}
                                    billingData={billingData}
                                />
                            )}
                        </div>

                        <div className="footer-compare">
                            <a
                                href="https://ghost.org/pricing/?ref=billing.ghost.org"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View full pricing page &rarr;
                            </a>
                        </div>
                    </footer>
                </>
            )}
        </main>
    );
}

Plans.propTypes = {
    showNav: PropTypes.func,
    updateTitle: PropTypes.func,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
};

export default withRouter(Plans);
